import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
    Package, CheckCircle2, Clock, AlertOctagon, FileEdit, Plus, 
    ArrowRight, ArrowLeft, RefreshCw, Eye, ExternalLink, ShieldAlert,
    TrendingUp, ShoppingBag, Video, Users, MessageSquare, Radio, BarChart3
} from 'lucide-react';
import { getProducts, getUserId, getOrdersByUser, getSellerChatThreads, liveAPI } from '../api/client';
import { isRTL } from '../utils/translations';
import Spinner from '../components/Spinner';

export default function SellerDashboard() {
    const navigate = useNavigate();
    const rtl = isRTL();

    const [products, setProducts] = useState([]);
    const [orders, setOrders] = useState([]);
    const [activeLiveCount, setActiveLiveCount] = useState(0);
    const [realUnreadCount, setRealUnreadCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [hasActiveDraft, setHasActiveDraft] = useState(false);

    const loadDashboardData = async () => {
        try {
            setRefreshing(true);
            const userId = getUserId() || localStorage.getItem('userId');
            const [prodsRes, ordersRes, liveRoomsRes, threadsRes] = await Promise.all([
                getProducts(0, 100, '', '', '', userId).catch(() => ({ data: [] })),
                userId ? getOrdersByUser(userId).catch(() => []) : Promise.resolve([]),
                liveAPI.getActiveRooms().catch(() => []),
                userId ? getSellerChatThreads(userId).catch(() => ({ data: [] })) : Promise.resolve({ data: [] })
            ]);

            if (prodsRes && prodsRes.data) {
                const list = prodsRes.data.content || (Array.isArray(prodsRes.data) ? prodsRes.data : []);
                setProducts(list);
            }
            if (Array.isArray(ordersRes)) {
                setOrders(ordersRes);
            }
            if (Array.isArray(liveRoomsRes)) {
                setActiveLiveCount(liveRoomsRes.length);
            }

            // Real unread messages calculation (Zero fake data)
            const threadsList = threadsRes?.data || (Array.isArray(threadsRes) ? threadsRes : []);
            const unreadSum = threadsList.reduce((acc, t) => acc + (t.unreadCount || (t.unread ? 1 : 0)), 0);
            setRealUnreadCount(unreadSum);
        } catch (err) {
            console.error('Failed to load seller dashboard products:', err);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        loadDashboardData();
        // Check for local draft
        try {
            const draft = localStorage.getItem('luxvono_seller_active_draft');
            if (draft) {
                const parsed = JSON.parse(draft);
                if (parsed && (parsed.nameAr || parsed.nameEn || parsed.videoUrl || parsed.images?.length > 0)) {
                    setHasActiveDraft(true);
                }
            }
        } catch (_) {}
    }, []);

    if (loading) {
        return (
            <div style={{ padding: '60px 0', display: 'flex', justifyContent: 'center' }}>
                <Spinner />
            </div>
        );
    }

    // Categorize metrics from actual backend data
    const publishedCount = products.filter(p => p.approvalStatus === 'APPROVED' || (p.active && p.approvalStatus !== 'REJECTED' && p.approvalStatus !== 'PENDING')).length;
    const pendingCount = products.filter(p => p.approvalStatus === 'PENDING' || p.approvalStatus === 'PENDING_REVIEW' || (!p.active && p.approvalStatus !== 'REJECTED')).length;
    const rejectedProducts = products.filter(p => p.approvalStatus === 'REJECTED');
    const draftCount = hasActiveDraft ? 1 : 0;

    // Aggregate real statistics
    const totalViews = products.reduce((acc, p) => acc + (Number(p.viewCount) || 0), 0);
    const ordersReceived = orders.length;

    // Followers count from store profile persistence (Zero fake data)
    let followersCount = 0;
    try {
        const raw = localStorage.getItem('luxvono_seller_store_profile');
        if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed.followersCount != null) followersCount = Number(parsed.followersCount);
        }
    } catch (_) {}

    // Real unread messages count from API
    const unreadMessagesCount = realUnreadCount;

    return (
        <div>
            {/* Header with Title, Analytics Link and Quick Add Button */}
            <div className="seller-dashboard-header">
                <div>
                    <h1 style={{ margin: '0 0 6px 0', fontSize: 24, fontWeight: 800, color: '#111827' }}>
                        {rtl ? 'لوحة تحكم التاجر' : 'Seller Dashboard'}
                    </h1>
                    <p style={{ margin: 0, fontSize: 14, color: '#6b7280' }}>
                        {rtl ? 'متابعة المنتجات، حالة المراجعة، مقاطع الفيديو، ومبيعات المتجر لحظياً.' : 'Track your listings, moderation statuses, videos, and sales in real-time.'}
                    </p>
                </div>

                <div className="seller-dashboard-actions-group">
                    <Link
                        to="/seller/analytics"
                        className="seller-btn-secondary"
                        style={{ color: '#111827', textDecoration: 'none' }}
                    >
                        <BarChart3 size={16} color="#111827" />
                        <span>{rtl ? 'التحليلات المتقدمة' : 'Full Analytics'}</span>
                    </Link>

                    <button 
                        onClick={loadDashboardData}
                        disabled={refreshing}
                        className="seller-btn-secondary"
                        style={{ color: '#111827' }}
                    >
                        <RefreshCw size={15} color="#111827" className={refreshing ? 'spin' : ''} />
                        <span>{rtl ? 'تحديث' : 'Refresh'}</span>
                    </button>

                    <Link 
                        to="/seller/products/new"
                        className="seller-btn-primary-gold"
                        style={{ textDecoration: 'none' }}
                    >
                        <Plus size={18} />
                        <span>{rtl ? 'إضافة منتج جديد' : 'Publish Product'}</span>
                    </Link>
                </div>
            </div>

            {/* Active Draft Alert */}
            {hasActiveDraft && (
                <div style={{ background: 'rgba(254,44,85,0.08)', border: '1px solid rgba(254,44,85,0.25)', borderRadius: 14, padding: '16px 20px', marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <FileEdit size={22} color="#FE2C55" />
                        <div>
                            <strong style={{ color: 'var(--seller-text, #111827)', fontSize: 14, display: 'block' }}>
                                {rtl ? 'لديك مسودة منتج محفوظة محلياً غير مكتملة' : 'You have an active saved product draft'}
                            </strong>
                            <span style={{ fontSize: 12, color: 'var(--seller-muted, #64748b)' }}>
                                {rtl ? 'تم حفظ كافة بيانات النموذج والفيديو تلقائياً لضمان عدم ضياع مجهودك.' : 'All entered details, videos, and images were automatically preserved locally.'}
                            </span>
                        </div>
                    </div>
                    <Link 
                        to="/seller/products/new?resume=true"
                        style={{ background: '#FE2C55', color: '#ffffff', padding: '8px 18px', borderRadius: 8, fontWeight: 700, fontSize: 13, textDecoration: 'none', boxShadow: '0 2px 10px rgba(254,44,85,0.35)' }}
                    >
                        {rtl ? 'متابعة تعديل المسودة' : 'Resume Draft'}
                    </Link>
                </div>
            )}

            {/* Rejection Notice Banner */}
            {rejectedProducts.length > 0 && (
                <div style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.35)', borderRadius: 14, padding: '20px', marginBottom: 28 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                        <ShieldAlert size={24} color="#f87171" />
                        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#f87171' }}>
                            {rtl ? `تنبيه مراجعة: تم رفض ${rejectedProducts.length} منتج يتطلب تعديلك` : `Moderation Notice: ${rejectedProducts.length} product(s) require modifications`}
                        </h3>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {rejectedProducts.map(p => (
                            <div key={p.id} style={{ background: '#12141a', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 10, padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                                <div>
                                    <strong style={{ color: '#ffffff', fontSize: 14 }}>{p.nameAr || p.name}</strong>
                                    <p style={{ margin: '4px 0 0 0', fontSize: 13, color: '#fca5a5' }}>
                                        {rtl ? 'سبب رفض الإدارة: ' : 'Admin Rejection Reason: '}
                                        <em>{p.rejectionReason || (rtl ? 'جودة الصور أو بيانات المنتج غير مطابقة للمواصفات الفاخرة.' : 'Details or image quality do not meet luxury guidelines.')}</em>
                                    </p>
                                </div>
                                <Link 
                                    to={`/seller/products/new?edit=${p.id}`}
                                    style={{ background: '#ef4444', color: '#fff', padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, textDecoration: 'none' }}
                                >
                                    {rtl ? 'تعديل وإعادة الإرسال للمراجعة' : 'Edit & Resubmit'}
                                </Link>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Core Analytics Cards Grid */}
            <div className="seller-grid-cards" style={{ marginBottom: 28 }}>
                {/* 1. Published Products */}
                <Link to="/seller/products/published" className="seller-stat-card" style={{ textDecoration: 'none' }}>
                    <div className="seller-stat-info">
                        <h4>{rtl ? 'المنتجات المنشورة' : 'Published Products'}</h4>
                        <p style={{ color: '#111827' }}>{publishedCount}</p>
                    </div>
                    <div className="seller-stat-icon" style={{ background: '#f0fdf4', color: '#10b981', border: '1px solid #bbf7d0' }}>
                        <Package size={22} />
                    </div>
                </Link>

                {/* 2. Total Views */}
                <Link to="/seller/analytics" className="seller-stat-card" style={{ textDecoration: 'none' }}>
                    <div className="seller-stat-info">
                        <h4>{rtl ? 'مشاهدات المنتجات' : 'Total Views'}</h4>
                        <p style={{ color: '#111827' }}>{totalViews.toLocaleString()}</p>
                    </div>
                    <div className="seller-stat-icon" style={{ background: '#f8fafc', color: '#111827', border: '1px solid #e2e8f0' }}>
                        <Eye size={22} />
                    </div>
                </Link>

                {/* 3. Orders Received */}
                <Link to="/seller/orders" className="seller-stat-card" style={{ textDecoration: 'none' }}>
                    <div className="seller-stat-info">
                        <h4>{rtl ? 'الطلبات المنجزة' : 'Orders Received'}</h4>
                        <p style={{ color: '#111827' }}>{ordersReceived}</p>
                    </div>
                    <div className="seller-stat-icon" style={{ background: '#f8fafc', color: '#111827', border: '1px solid #e2e8f0' }}>
                        <ShoppingBag size={22} />
                    </div>
                </Link>

                {/* 4. Followers */}
                <Link to="/seller/store" className="seller-stat-card" style={{ textDecoration: 'none' }}>
                    <div className="seller-stat-info">
                        <h4>{rtl ? 'المتابعون' : 'Followers'}</h4>
                        <p style={{ color: '#111827' }}>{followersCount.toLocaleString()}</p>
                    </div>
                    <div className="seller-stat-icon" style={{ background: '#f8fafc', color: '#111827', border: '1px solid #e2e8f0' }}>
                        <Users size={22} />
                    </div>
                </Link>

                {/* 5. Messages */}
                <Link to="/seller/messages" className="seller-stat-card" style={{ textDecoration: 'none' }}>
                    <div className="seller-stat-info">
                        <h4>{rtl ? 'الرسائل والمحادثات' : 'Messages & Chat'}</h4>
                        <p style={{ color: '#111827' }}>{unreadMessagesCount}</p>
                    </div>
                    <div className="seller-stat-icon" style={{ background: unreadMessagesCount > 0 ? '#fff1f2' : '#f8fafc', color: unreadMessagesCount > 0 ? '#FE2C55' : '#111827', border: unreadMessagesCount > 0 ? '1px solid #fecdd3' : '1px solid #e2e8f0' }}>
                        <MessageSquare size={22} />
                    </div>
                </Link>

                {/* 6. Active Live Streams */}
                <Link to="/seller/live" className="seller-stat-card" style={{ textDecoration: 'none' }}>
                    <div className="seller-stat-info">
                        <h4>{rtl ? 'البث المباشر (Live Streams)' : 'Active Live Streams'}</h4>
                        <p style={{ color: activeLiveCount > 0 ? '#FE2C55' : '#111827' }}>{activeLiveCount}</p>
                    </div>
                    <div className="seller-stat-icon" style={{ background: activeLiveCount > 0 ? '#fff1f2' : '#f8fafc', color: '#FE2C55', border: activeLiveCount > 0 ? '1px solid #fecdd3' : '1px solid #e2e8f0' }}>
                        <Radio size={22} />
                    </div>
                </Link>
            </div>

            {/* Moderation Status Pills */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap', width: '100%', boxSizing: 'border-box' }}>
                <Link 
                    to="/seller/products/pending" 
                    style={{ background: '#ffffff', border: '1px solid #e9ecef', borderRadius: 10, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', color: '#111827', fontSize: 13, boxShadow: '0 1px 3px rgba(0,0,0,0.03)', boxSizing: 'border-box' }}
                >
                    <Clock size={16} color="#f59e0b" />
                    <span>{rtl ? 'قيد المراجعة: ' : 'Pending Review: '}<strong style={{ color: '#f59e0b' }}>{pendingCount}</strong></span>
                </Link>

                <Link 
                    to="/seller/products/rejected" 
                    style={{ background: '#ffffff', border: '1px solid #fecdd3', borderRadius: 10, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', color: '#111827', fontSize: 13, boxShadow: '0 1px 3px rgba(0,0,0,0.03)', boxSizing: 'border-box' }}
                >
                    <AlertOctagon size={16} color="#FE2C55" />
                    <span>{rtl ? 'يتطلب تعديلات: ' : 'Requires Modifications: '}<strong style={{ color: '#FE2C55' }}>{rejectedProducts.length}</strong></span>
                </Link>

                <Link 
                    to="/seller/products/drafts" 
                    style={{ background: '#ffffff', border: '1px solid #e9ecef', borderRadius: 10, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', color: '#111827', fontSize: 13, boxShadow: '0 1px 3px rgba(0,0,0,0.03)', boxSizing: 'border-box' }}
                >
                    <FileEdit size={16} color="#111827" />
                    <span>{rtl ? 'المسودات: ' : 'Drafts: '}<strong style={{ color: '#111827' }}>{draftCount}</strong></span>
                </Link>
            </div>

            {/* Recent Products Table */}
            <div className="seller-recent-table-card">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                    <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#111827' }}>
                        {rtl ? 'أحدث المنتجات المعروضة' : 'Recent Product Catalog'}
                    </h3>
                    <Link to="/seller/products" style={{ color: '#111827', fontSize: 13, fontWeight: 600, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span>{rtl ? 'عرض كافة المنتجات' : 'View All Products'}</span>
                        {rtl ? <ArrowLeft size={14} /> : <ArrowRight size={14} />}
                    </Link>
                </div>

                {products.length === 0 ? (
                    <div style={{ padding: '40px 0', textAlign: 'center', color: '#6b7280' }}>
                        <Package size={40} style={{ opacity: 0.3, marginBottom: 12, color: '#111827' }} />
                        <p style={{ margin: 0, fontSize: 14 }}>{rtl ? 'لم تقم بنشر أي منتجات بعد.' : 'No products uploaded yet.'}</p>
                        <Link 
                            to="/seller/products/new" 
                            style={{ display: 'inline-block', marginTop: 14, color: '#FE2C55', fontWeight: 700, fontSize: 13, textDecoration: 'none' }}
                        >
                            {rtl ? '+ ابدأ بنشر منتجك الأول الآن' : '+ Publish your first item'}
                        </Link>
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto', width: '100%', boxSizing: 'border-box' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: rtl ? 'right' : 'left', fontSize: 13 }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid #e9ecef', color: '#6b7280' }}>
                                    <th style={{ padding: '12px 14px', fontWeight: 600 }}>{rtl ? 'المنتج' : 'Product'}</th>
                                    <th style={{ padding: '12px 14px', fontWeight: 600 }}>{rtl ? 'القسم' : 'Category'}</th>
                                    <th style={{ padding: '12px 14px', fontWeight: 600 }}>{rtl ? 'السعر' : 'Price'}</th>
                                    <th style={{ padding: '12px 14px', fontWeight: 600 }}>{rtl ? 'الحالة' : 'Status'}</th>
                                    <th style={{ padding: '12px 14px', fontWeight: 600 }}>{rtl ? 'إجراءات' : 'Actions'}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {products.slice(0, 5).map(prod => {
                                    const isPending = prod.approvalStatus === 'PENDING' || prod.approvalStatus === 'PENDING_REVIEW' || (!prod.active && prod.approvalStatus !== 'REJECTED');
                                    const isRejected = prod.approvalStatus === 'REJECTED';
                                    const isApproved = prod.approvalStatus === 'APPROVED' || (prod.active && !isRejected && !isPending);

                                    return (
                                        <tr key={prod.id} style={{ borderBottom: '1px solid #f1f3f5' }}>
                                            <td style={{ padding: '14px', fontWeight: 600, color: '#111827' }}>
                                                {prod.nameAr || prod.name}
                                            </td>
                                            <td style={{ padding: '14px', color: '#6b7280' }}>
                                                {prod.categoryName || prod.category?.name || 'General'}
                                            </td>
                                            <td style={{ padding: '14px', color: '#111827', fontWeight: 700 }}>
                                                {prod.price} {prod.currency || 'SAR'}
                                            </td>
                                            <td style={{ padding: '14px' }}>
                                                {isApproved && <span className="status-badge published">PUBLISHED</span>}
                                                {isPending && <span className="status-badge pending">PENDING</span>}
                                                {isRejected && <span className="status-badge rejected">REJECTED</span>}
                                            </td>
                                            <td style={{ padding: '14px' }}>
                                                <div style={{ display: 'flex', gap: 8 }}>
                                                    {isApproved && (
                                                        <Link 
                                                            to={`/products/${prod.id}`}
                                                            target="_blank"
                                                            style={{ color: '#111827', padding: '4px 8px', borderRadius: 6, background: '#f3f4f6', border: '1px solid #e5e7eb', textDecoration: 'none', fontSize: 12, fontWeight: 600 }}
                                                        >
                                                            {rtl ? 'معاينة' : 'View'}
                                                        </Link>
                                                    )}
                                                    <Link 
                                                        to={`/seller/products/new?edit=${prod.id}`}
                                                        style={{ color: '#111827', padding: '4px 8px', borderRadius: 6, background: '#f3f4f6', border: '1px solid #e5e7eb', textDecoration: 'none', fontSize: 12, fontWeight: 600 }}
                                                    >
                                                        {rtl ? 'تعديل' : 'Edit'}
                                                    </Link>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
